import type { EntityModifyParameters, ExtendedEntityParameters } from 'src/components/EntityDetail';
import { gqlClient } from 'src/util/gql-client';
import type { ValidationResult } from 'types';
import {
    AddAssetTypeDocument,
    type FisCoreStateEnum,
    UpdateAssetTypeDocument,
} from 'types/db';
import { validatePerformanceEntity } from './performance.validations';
import { getListChanges } from 'src/util/composables/watch-list-changes';

type AssetCodePlc = {
    id: string,
    asset_type_id: string,
    data_label_id: string,
    code: number,
    description: string,
    meta: any,
    core_state: FisCoreStateEnum,
    __typename?: string,
};

export const createPerformanceEntity = async (payload: EntityModifyParameters<'assetType'>): Promise<ValidationResult> => {
    const { entity } = payload;
    const validationResult = validatePerformanceEntity(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: AddAssetTypeDocument,
            variables: {
                assetType: {
                    id: entity.id,
                    name: entity.name,
                    asset_codes: {
                        data: entity.asset_codes.map(ac => ({
                            id: ac.id,
                            data_label_id: ac.data_label_id,
                            code: ac.code,
                            description: ac.description,
                            core_state: ac.core_state,
                            meta: ac.meta,
                        })),
                    },
                },
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Asset: "${JSON.stringify(err)}"`,
        };
    }
};

export const savePerformanceEntity = async (payload: ExtendedEntityParameters<'assetType'>): Promise<ValidationResult> => {
    const { entity, oldEntity: originalOldEntity } = payload;

    const validationResult = validatePerformanceEntity(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    const origAssetCodes: AssetCodePlc[] = [ ...originalOldEntity?.asset_codes || [] ];
    const newAssetCodes: AssetCodePlc[] = [ ...entity?.asset_codes || [] ];
    const assetCodesPlc = getListChanges(newAssetCodes, origAssetCodes, { deep: true, partialChanges: false, format: 'plc' });

    try {
        await gqlClient.request({
            document: UpdateAssetTypeDocument,
            variables: {
                assetTypeId: entity.id,
                assetType: {
                    name: entity.name,
                },
                insertAssetCodes: [
                    ...assetCodesPlc.inserts.map(ac => ({
                        id: ac.id,
                        data_label_id: ac.data_label_id,
                        asset_type_id: ac.asset_type_id,
                        code: ac.code,
                        description: ac.description,
                        meta: ac.meta,
                        core_state: ac.core_state,
                    })),
                    ...assetCodesPlc.updates.map(ac => ({
                        id: ac.id,
                        data_label_id: ac.data_label_id,
                        asset_type_id: ac.asset_type_id,
                        code: ac.code,
                        description: ac.description,
                        meta: ac.meta,
                        core_state: ac.core_state,
                    })),
                ],
                deleteAssetCodeBoolExp: {
                    _or: assetCodesPlc.deletes.map(ac => ({
                        asset_type_id: { _eq: ac.asset_type_id },
                        data_label_id: { _eq: ac.data_label_id },
                        code: { _eq: ac.code },
                    })),
                },
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Asset: "${JSON.stringify(err)}"`,
        };
    }
};
